import React, { memo, useMemo } from "react";
import { useParams } from "react-router-dom";
import { round, isNull } from "lodash";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import Link from "@mui/material/Link";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import DoNotTouchOutlinedIcon from "@mui/icons-material/DoNotTouchOutlined";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import {
  prettyNumericRound,
  getFormattedNumber,
  singleValue,
  isBulkFootage,
  getFootageValue,
  BULK_FOOTAGE,
  isValidMissingSku,
} from "../../../utils";
import useStableCallback from "../../../hooks/useStableCallback";
import { validPog } from "../../../utils/pogValidator";
import { isItemDepartment } from "../../../utils/deptValidator";
import NumericLabel from "../../../components/NumericLabel";
import config from "../../../config";

const CustomPogSx = {
  fontSize: "0.75rem",
  paddingTop: "0px !important",
  alignSelf: "center",
  marginTop: "5px",
  marginBottom: "5px",
  paddingLeft: "10px",

  "& .MuiCheckbox-root": {
    padding: "0px",
    paddingRight: "5px",
  },
  "& .pog-title": {
    display: "inline-flex",
    verticalAlign: "unset",
    fontWeight: 600,

    "& .pog-name": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  "& .truncated-value": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .bold-label": { fontWeight: 500 },
  "& .fill-space-dash": {
    display: "inline-block",
    width: "45px",
  },
};

const MissingSkuZeroSx = {
  span: {
    backgroundColor: "yellow",
    fontWeight: 600,
  },
};

const HazmatRestrictedIconSx = {
  fontSize: "18px",
  color: "#ff5722",
  marginRight: "5px",
};

const CollapsedPogView = ({
  data,
  handleProcessData,
  isDuplicateSelection = false,
  freezePog = false,
}) => {
  const { departmentLetter } = useParams();

  const isPogChecked = data?.selection;
  const isPogSelected = data?.Selected;

  const isPartOfItemsDept = isItemDepartment(data);

  const handleChange = useStableCallback(
    (field, isChecked, isSelected) => (event) => {
      if (field === "selection") {
        handleProcessData({
          field,
          value: event?.target?.checked,
          isPogChecked: isChecked,
          isPogSelected: isSelected,
        });
      } else if (field === "Selected") {
        handleProcessData({
          field,
          value: event?.target?.value,
          isPogChecked: isChecked,
          isPogSelected: isSelected,
        });
      } else {
        let newValue = String(event?.target?.value).trim();
        let currentValue = null;
        if (newValue.split(".").length > 1) {
          const n = Number(newValue);
          if (n <= 100 || !isNaN(n)) {
            currentValue = newValue; // it keeps value as is without any round off
          } else {
            return event.preventDefault();
          }
        } else {
          currentValue = !isNull(newValue) ? Number(newValue) : null;
          if (currentValue > 100 || isNaN(currentValue)) {
            return event.preventDefault();
          }
        }
        handleProcessData({
          field,
          value: currentValue,
          isPogChecked: isChecked,
          isPogSelected: isSelected,
        });
      }
    }
  );

  const handleAdjLinealBlur = useStableCallback((event) => {
    const newValue = event.target.value;
    if (String(newValue)?.indexOf(".") !== -1) {
      handleProcessData({
        field: "AdjLineal",
        value: prettyNumericRound(newValue, 1),
      });
    }
  });

  const selectedNo = data?.Selected === singleValue.no;

  const isBulkFt = isBulkFootage(data?.LinealFootage);

  const blockPog =
    !validPog(data) ||
    data?.CustomReleaseStatus || // if item has at least 1 item released then checkbox and dropdown of Y freezes
    freezePog ||
    isDuplicateSelection;

  const memoizedFootageValue = useMemo(() => {
    if (!!data?.LinealFootage) {
      if (isBulkFt) {
        return BULK_FOOTAGE;
      }
      return getFootageValue(data?.LinealFootage);
    }
    return "0ft";
  }, [data?.LinealFootage, isBulkFt]);

  const defaultAdjLinealValue = useMemo(() => {
    return isBulkFt ? BULK_FOOTAGE : data?.LinealFootage;
  }, [data?.LinealFootage, isBulkFt]);

  const isHazmatRestricted = useMemo(() => {
    return data?.HazmatInd || data?.RestrictedInd;
  }, [data?.HazmatInd, data?.RestrictedInd]);

  return (
    <Grid
      container
      item
      xs
      sx={CustomPogSx}
      alignItems="center"
      justifyContent="space-between"
    >
      <Grid item xs="auto" sx={{ marginRight: "-26px" }}>
        <Checkbox
          size="small"
          disableRipple
          checked={data?.selection}
          disabled={blockPog}
          onClick={handleChange("selection", isPogChecked, isPogSelected)}
        />
      </Grid>
      <Grid
        item
        xs="auto"
        md={isHazmatRestricted ? 1.28 : 1.8}
        lg={isHazmatRestricted ? 2.28 : 2.8}
        className="pog-title"
        mr={isHazmatRestricted ? 0.28 : 0.5}
      >
        {!isPartOfItemsDept ? (
          <Link
            href={config.PogFileBasePath.concat(data?.PlanogramPDF)}
            target="_blank"
            rel="noreferrer noopener"
            mr={0.2}
          >
            {data?.PlanogramNumber}
          </Link>
        ) : (
          <Link underline="none">{data?.PlanogramNumber}</Link>
        )}
        -
        <Tooltip title={data?.PlanogramName} arrow>
          <span className="pog-name">
            <Link
              href={`/manage-planograms/items/${departmentLetter}/${data?.PlanogramNumber}`}
              ml={0.2}
            >
              {data?.PlanogramName}
            </Link>
          </span>
        </Tooltip>
      </Grid>
      {isHazmatRestricted && (
        <>
          <Grid item xs="auto" md={0.3} lg={0.3} className="pog-title" mr={0.5}>
            {data?.HazmatInd && (
              <Tooltip title="Hazmat" arrow>
                <LocalFireDepartmentOutlinedIcon sx={HazmatRestrictedIconSx} />
              </Tooltip>
            )}
            {data?.RestrictedInd && (
              <Tooltip title="Restricted" arrow>
                <DoNotTouchOutlinedIcon sx={HazmatRestrictedIconSx} />
              </Tooltip>
            )}
          </Grid>
        </>
      )}
      <Grid item xs="auto" mr={0.5}>
        <span className="bold-label">Sel: </span>
        {data?.AllItemsFromFillIn ? (
          <span className="fill-space-dash"> -</span>
        ) : (
          <CustomDropdown
            value={data?.Selected}
            inputsx={{ fontSize: "10px" }}
            onChange={handleChange("Selected", isPogChecked, isPogSelected)}
            disabled={blockPog || isPartOfItemsDept}
          >
            <option value="N">N</option>
            <option value="Y">Y</option>
            <option value="E">E</option>
          </CustomDropdown>
        )}
      </Grid>
      <Grid item xs="auto" md={0.8} mr={0.1}>
        <Tooltip title={data?.Region ?? ""} arrow>
          <div className="truncated-value">
            {!!data?.Region ? data?.Region : "-"}
          </div>
        </Tooltip>
      </Grid>
      <Grid item xs="auto" md={0.8} mr={0.1}>
        <Tooltip title={data?.Urbanicity ?? ""} arrow>
          <div className="truncated-value">
            {!!data?.Urbanicity ? data?.Urbanicity : "-"}
          </div>
        </Tooltip>
      </Grid>
      <Grid item xs="auto" md={0.4} lg={0.5} mr={0.5}>
        {isPartOfItemsDept ? "-" : memoizedFootageValue}
      </Grid>
      <Grid item xs="auto" mr={1}>
        {data?.AllItemsFromFillIn || isPartOfItemsDept ? (
          <span className="fill-space-dash"> -</span>
        ) : (
          <>
            <CustomInput
              sx={{ width: "35px", marginRight: isBulkFt ? "7px" : 0 }}
              value={
                isNull(data?.AdjLineal)
                  ? defaultAdjLinealValue
                  : data?.AdjLineal
              }
              disabled={blockPog || selectedNo || isBulkFt}
              onChange={handleChange("AdjLineal")}
              onBlur={handleAdjLinealBlur}
            />
            {!isBulkFt ? "ft" : ""}
          </>
        )}
      </Grid>
      <Grid item xs="auto" md={0.5} lg={0.8} mr={0.5}>
        <NumericLabel isSeparator value={round(data?.SKUs || 0, 2)} />
      </Grid>
      <Grid item xs="auto" md={0.9} lg={1} mr={0.5}>
        ${getFormattedNumber(prettyNumericRound(data?.Cost || 0))}
      </Grid>
      <Grid
        item
        xs="auto"
        md={0.6}
        lg={0.9}
        mr={0.5}
        sx={isValidMissingSku(data) ? MissingSkuZeroSx : {}}
      >
        <NumericLabel isSeparator value={round(data?.MissingSkus || 0, 2)} />
      </Grid>
      <Grid item xs="auto" md={0.9} lg={1}>
        {data?.AllItemsFromFillIn ? (
          <span className="fill-space-dash"> -</span>
        ) : (
          `${getFormattedNumber(prettyNumericRound(data?.AdjCost || 0))}`
        )}
      </Grid>
    </Grid>
  );
};

export default memo(CollapsedPogView);
